import { ChangeEvent, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ViewItem } from "../../../Components/Views/ViewItem";
import { controllers } from "../../../Controllers/Controllers";
import { navigator } from "../../../Controllers/Navigator";
import { STATUS_LABELS, LeadCategoryType, LeadType, LeadsPagination } from "../../../DataTypes/leads";
import { flipTooltipIfNeeded, getRoundedLocalDateTimeString } from "../../../Helpers/Helpers";
import './outreach-styles.css'
import { SourceSelectView } from "../../UIComponents/SourceSelectView";

const OUTREACH_METHOD_LABELS: Record<string, string> = {
    site_form: 'Website Form',
    email: 'Email',
    linkedin: 'LinkedIn',
    phone: 'Phone Call',
    other: 'Other',
}

const OUTREACH_METHOD_OPTIONS = ['email', 'linkedin', 'phone', 'other']

const PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 350

type SourceFilter = 'all' | 'outreach' | 'research'

const SOURCE_FILTER_VALUES: SourceFilter[] = ['all', 'outreach', 'research']

type LeadManagerType = {
    id: number;
    email: string;
    username: string;
}

const parseFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search)
    const page = Number(params.get('page'))
    const status = params.get('status') || 'all'
    const source = params.get('source') || 'all'
    return {
        page: Number.isInteger(page) && page > 0 ? page : 1,
        status: status in STATUS_LABELS ? status : 'all',
        search: params.get('search') || '',
        source: (SOURCE_FILTER_VALUES as string[]).includes(source) ? source as SourceFilter : 'all',
        hasWebsite: params.get('website') === '1',
        hasContactEmail: params.get('email') === '1',
        assignedTo: params.get('assigned_to') || '',
    }
}

type OutreachPageState = {
    leads: LeadType[];
    categories: LeadCategoryType[];
    leadManagers: LeadManagerType[];
    pagination: LeadsPagination | null;
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    page: number;
    status: string;
    search: string;
    source: SourceFilter;
    hasWebsite: boolean;
    hasContactEmail: boolean;
    assignedTo: string;
    sentModalLead: LeadType | null;
    updatingLeadId: number | null;
    focusedLeadId: null;
}

class OutreachPage extends ViewItem {
    state: OutreachPageState;
    searchTimer: any = null;

    constructor(props: any) {
        super(props)
        const urlFilters = parseFiltersFromUrl()
        this.state = {
            leads: [],
            categories: [],
            leadManagers: [],
            pagination: null,
            loading: true,
            error: null,
            needsAuth: false,
            sentModalLead: null,
            updatingLeadId: null,
            focusedLeadId: null,
            ...urlFilters,
        }
    }

    componentDidMount(): void {
        this.syncUrlWithState()
        this.loadLeads()
        this.loadCategories()
        if (controllers().auth.isAdmin) {
            this.loadLeadManagers()
        }
    }

    componentWillUnmount(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer)
    }

    syncUrlWithState = () => {
        const { page, status, search, source, hasWebsite, hasContactEmail, assignedTo } = this.state
        const params = new URLSearchParams()
        if (page > 1) params.set('page', String(page))
        if (status !== 'all') params.set('status', status)
        if (search) params.set('search', search)
        if (source !== 'all') params.set('source', source)
        if (hasWebsite) params.set('website', '1')
        if (hasContactEmail) params.set('email', '1')
        if (controllers().auth.isAdmin && assignedTo) params.set('assigned_to', assignedTo)
        const qs = params.toString()
        const newUrl = window.location.pathname + (qs ? `?${qs}` : '')
        window.history.replaceState(null, '', newUrl)
    }

    applyFilters = () => {
        this.syncUrlWithState()
        this.loadLeads()
    }

    loadCategories = async () => {
        const response = await controllers().outreach.getCategories()
        if (response && response.statusCode === 200) {
            this.setState({ categories: response.data?.categories || [] })
        }
    }

    loadLeadManagers = async () => {
        const response = await controllers().outreach.getLeadManagers()
        if (response && response.statusCode === 200) {
            this.setState({ leadManagers: response.data?.managers || [] })
        }
    }

    handleRowFocus = (leadId:any) => {
        this.setState({ focusedLeadId: leadId });
    };

    loadLeads = async () => {
        const { page, status, search, source, hasWebsite, hasContactEmail, assignedTo } = this.state
        this.setState({ loading: true, error: null, needsAuth: false })
        try {
            if (!controllers().auth.userToken) {
                this.setState({ loading: false, needsAuth: true })
                return
            }
            const response = await controllers().outreach.getLeads({
                page,
                per_page: PER_PAGE,
                status: status !== 'all' ? status : undefined,
                search: search || undefined,
                // 'all' groups research-sourced leads first (newest within
                // each group) rather than interleaving both sources by date.
                is_from_research: source === 'all' ? undefined : source === 'research',
                // sort: source === 'all' ? 'source' : undefined,
                has_website: hasWebsite || undefined,
                has_contact_email: hasContactEmail || undefined,
                assigned_to_id: (controllers().auth.isAdmin && assignedTo) ? Number(assignedTo) : undefined,
            })
            if (!response || response.statusCode !== 200) {
                this.setState({
                    loading: false,
                    error: response?.error || 'Failed to load leads.',
                })
                return
            }
            this.setState({
                leads: response.data?.leads || [],
                pagination: response.data?.pagination || null,
                loading: false,
            })
        } catch (e) {
            this.setState({ loading: false, error: 'Failed to load leads.' })
        }
    }

    handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        this.setState({ search: value })
        if (this.searchTimer) clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, this.applyFilters)
        }, SEARCH_DEBOUNCE_MS)
    }

    handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ status: e.target.value, page: 1 }, this.applyFilters)
    }

    handleSourceChange = (value: SourceFilter) => {
        this.setState({ source: value, page: 1 }, this.applyFilters)
    }

    handleHasWebsiteChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ hasWebsite: e.target.checked, page: 1 }, this.applyFilters)
    }

    handleHasContactEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ hasContactEmail: e.target.checked, page: 1 }, this.applyFilters)
    }

    handleAssignedToChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ assignedTo: e.target.value, page: 1 }, this.applyFilters)
    }

    goToPage = (page: number) => {
        const { pagination } = this.state
        if (pagination && (page < 1 || page > pagination.total_pages)) return
        this.setState({ page }, this.applyFilters)
    }

    updateLeadLocally = (leadId: number, fields: Partial<LeadType>) => {
        this.setState((prev: OutreachPageState) => ({
            leads: prev.leads.map(lead => lead.id === leadId ? { ...lead, ...fields } : lead)
        }))
    }

    openSentToggle = (lead: LeadType) => {
        if (lead.outreach_done) {
            this.markUnsent(lead)
        } else {
            this.setState({ sentModalLead: lead })
        }
    }

    closeSentModal = () => {
        this.setState({ sentModalLead: null })
    }

    markUnsent = async (lead: LeadType) => {
        this.setState({ updatingLeadId: lead.id })
        const response = await controllers().outreach.editLead(lead.id, { outreach_done: false })
        if (response && response.statusCode === 200) {
            this.updateLeadLocally(lead.id, response.data || { outreach_done: false })
        }
        this.setState({ updatingLeadId: null })
    }

    confirmSent = async (method: string) => {
        const { sentModalLead } = this.state
        if (!sentModalLead) return
        this.setState({ updatingLeadId: sentModalLead.id, sentModalLead: null })
        const response = await controllers().outreach.editLead(sentModalLead.id, {
            outreach_done: true,
            outreach_method: method,
        })
        if (response && response.statusCode === 200) {
            this.updateLeadLocally(sentModalLead.id, response.data || { outreach_done: true, outreach_method: method })
        }
        this.setState({ updatingLeadId: null })
    }

    handleCategoryChange = async (lead: LeadType, categoryId: string) => {
        this.setState({ updatingLeadId: lead.id })
        const response = await controllers().outreach.editLead(lead.id, {
            category_id: categoryId ? Number(categoryId) : null,
        })
        if (response && response.statusCode === 200) {
            this.updateLeadLocally(lead.id, response.data || {})
        }
        this.setState({ updatingLeadId: null })
    }

    handleStatusChangeInline = async (lead: LeadType, newStatus: string) => {
        this.setState({ updatingLeadId: lead.id })
        const response = await controllers().outreach.updateLeadStatus(lead.id, newStatus)
        if (response && response.statusCode === 200) {
            this.updateLeadLocally(lead.id, response.data || { status: newStatus })
        }
        this.setState({ updatingLeadId: null })
    }

    handleNotesHover = (e: MouseEvent<HTMLDivElement>) => {
        flipTooltipIfNeeded(e.currentTarget)
    }

    renderSentModal = () => {
        const { sentModalLead } = this.state
        if (!sentModalLead) return null
        return (
            <div className="outreach-modal-overlay" onClick={this.closeSentModal}>
                <div className="outreach-modal" onClick={(e) => e.stopPropagation()}>
                    <h3 className="outreach-modal-title">Mark as contacted</h3>
                    <p className="outreach-modal-subtitle">
                        How did you reach out to <strong>{sentModalLead.company_name}</strong>?
                    </p>
                    <div className="outreach-modal-options">
                        {OUTREACH_METHOD_OPTIONS.map(method => (
                            <button
                                key={method}
                                className="outreach-modal-option"
                                onClick={() => this.confirmSent(method)}
                            >
                                {OUTREACH_METHOD_LABELS[method]}
                            </button>
                        ))}
                    </div>
                    <button className="outreach-modal-cancel" onClick={this.closeSentModal}>
                        Cancel
                    </button>
                </div>
            </div>
        )
    }

    renderPagination = () => {
        const { pagination, page } = this.state
        if (!pagination || pagination.total_pages <= 1) return null
        return (
            <div className="outreach-pagination">
                <span className="outreach-pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_count} total
                </span>
                <div className="outreach-pagination-controls">
                    <button
                        className="outreach-pagination-btn"
                        disabled={!pagination.has_previous}
                        onClick={() => this.goToPage(page - 1)}
                    >
                        Previous
                    </button>
                    <button
                        className="outreach-pagination-btn"
                        disabled={!pagination.has_next}
                        onClick={() => this.goToPage(page + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        )
    }

    renderTable = () => {
        const { leads, categories, updatingLeadId } = this.state
        return (
            <div className="outreach-table-wrap">
                <table className="outreach-table">
                    <thead>
                        <tr>
                            <th>Company</th>
                            <th>Notes</th>
                            <th>Category</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Sent</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map(lead => (
                            <tr
                                key={lead.id}
                                className={`tr-focus-lead${this.state.focusedLeadId === lead.id ? ' lead-is-focused' : ''}`}
                                onClick={() => this.handleRowFocus(lead.id)}
                            >
                                <td>
                                    <div className="outreach-company-cell">
                                        <div className="outreach-company-name-row">
                                            <span className="outreach-company-name">{lead.company_name}</span>
                                            {lead.is_from_research && (
                                                <span className="outreach-source-badge" title="Found via Research">Research</span>
                                            )}
                                            {lead.company_website_url && (
                                                <a
                                                    href={lead.company_website_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={lead.company_website_url}
                                                >
                                                    ↗
                                                </a>
                                            )}
                                            {lead.google_maps_url && (
                                                <a
                                                    href={lead.google_maps_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={lead.google_maps_url}
                                                >
                                                    G
                                                </a>
                                            )}
                                        </div>
                                        {lead.decision_maker_name && (
                                            <div className="outreach-dm-inline">
                                                {lead.decision_maker_name}
                                                {lead.decision_maker_title && ` · ${lead.decision_maker_title}`}
                                            </div>
                                        )}
                                        {(lead.company_email || lead.decision_maker_email) && (
                                            <div className="outreach-dm-inline">
                                                {lead.company_email || lead.decision_maker_email}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    {lead.notes ? (
                                        <div className="outreach-notes-cell" onMouseEnter={this.handleNotesHover}>
                                            <span className="outreach-notes-truncated">{lead.notes}</span>
                                            <div className="outreach-notes-tooltip">{lead.notes}</div>
                                        </div>
                                    ) : (
                                        <span className="outreach-muted">—</span>
                                    )}
                                </td>
                                <td>
                                    <select
                                        className="outreach-inline-select"
                                        value={lead.category?.id || ''}
                                        onChange={(e) => this.handleCategoryChange(lead, e.target.value)}
                                        disabled={updatingLeadId === lead.id}
                                    >
                                        <option value="">No category</option>
                                        {categories.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <select
                                        className={`lead-status-select lead-status-${lead.status}`}
                                        value={lead.status}
                                        onChange={(e) => this.handleStatusChangeInline(lead, e.target.value)}
                                        disabled={updatingLeadId === lead.id}
                                    >
                                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="outreach-muted">{getRoundedLocalDateTimeString(lead.created_at)}</td>
                                <td>
                                    <button
                                        className={`outreach-toggle ${lead.outreach_done ? 'outreach-toggle-on' : ''}`}
                                        onClick={() => this.openSentToggle(lead)}
                                        disabled={updatingLeadId === lead.id}
                                        aria-label={lead.outreach_done ? 'Mark as not sent' : 'Mark as sent'}
                                        title={lead.outreach_done ? 'Sent — click to undo' : 'Not sent yet'}
                                    >
                                        <span className="outreach-toggle-knob" />
                                    </button>
                                </td>
                                <td>
                                    <div className="row-buttons-box">
                                        <Link
                                            className="outreach-edit-btn"
                                            to={`/outreach/${lead.id}/edit`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Edit
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    render(): ReactNode {
        const { pagination, loading, error, needsAuth, status, search, hasWebsite, hasContactEmail, assignedTo, leads, leadManagers } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Outreach</h1>
                        <p className="outreach-subtitle">
                            {pagination ? `${pagination.total_count} lead${pagination.total_count === 1 ? '' : 's'}` : 'Leads'}
                        </p>
                    </div>

                    <div className="outreach-toolbar">
                        <input
                            type="text"
                            className="outreach-search"
                            placeholder="Search company, contact, or email..."
                            value={search}
                            onChange={this.handleSearchChange}
                        />
                        <select className="outreach-status-select" value={status} onChange={this.handleStatusChange}>
                            <option value="all">All statuses</option>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <label className="outreach-filter-checkbox">
                            <input type="checkbox" checked={hasWebsite} onChange={this.handleHasWebsiteChange} />
                            Website
                        </label>
                        <label className="outreach-filter-checkbox">
                            <input type="checkbox" checked={hasContactEmail} onChange={this.handleHasContactEmailChange} />
                            Email
                        </label>

                        {controllers().auth.isAdmin && (
                            <select
                                className="outreach-status-select"
                                value={assignedTo}
                                onChange={this.handleAssignedToChange}
                            >
                                <option value="">All lead managers</option>
                                {leadManagers.map(m => (
                                    <option key={m.id} value={m.id}>{m.username || m.email}</option>
                                ))}
                            </select>
                        )}

                        <SourceSelectView
                            source={this.state.source}
                            onSourceChange={this.handleSourceChange}
                        />
                    </div>
                    <button
                        className="outreach-add-btn"
                        onClick={() => navigator().navigate('/outreach/add')}
                    >
                        + Add New Lead
                    </button>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to view your leads.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadLeads}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !error && !needsAuth && leads.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No leads found.
                    </div>
                )}

                {!loading && !error && !needsAuth && leads.length > 0 && (
                    <>
                        {this.renderTable()}
                        {this.renderPagination()}
                    </>
                )}

                {this.renderSentModal()}
            </div>
        )
    }
}

export { OutreachPage }